/**
 * src/sync/outbox.js — Single-leader IndexedDB outbox for shadow-write retries (B3).
 *
 * Why this exists
 * ----------------
 * When `dualWrite()` fails (API down / network error / 5xx), the Firebase write
 * has already succeeded — we must NOT lose the envelope. We enqueue it here so
 * a background drainer can replay it later. The Firestore data is canonical, so
 * the outbox is purely best-effort to eventually mirror writes into PostgreSQL.
 *
 * Why a leader (B3)
 * -----------------
 * IndexedDB is shared across tabs of the same origin. Two tabs draining the
 * same queue concurrently produce duplicate POSTs. `navigator.locks` lets us
 * elect exactly one drainer. Other tabs enqueue normally and use
 * `BroadcastChannel('outbox')` to notify the leader that fresh work has arrived.
 *
 * Backoff (per spec)
 * ------------------
 * Per item: 1s, 4s, 16s, 64s, max 5min. Max 10 attempts; then mark the item
 * `requires_manual_review = true` and stop touching it (it stays visible for
 * triage in IndexedDB).
 *
 * Web Locks fallback
 * -------------------
 * Safari < 15.4 / older browsers without `navigator.locks` fall back to a
 * single-tab mode (every tab thinks it's the leader). This is acceptable
 * because the .NET endpoint is idempotent (LWW by serverWriteTs) — at worst
 * we send duplicate POSTs that resolve to `skipped_older`. Logged on startup.
 *
 * SSR / test fallback
 * --------------------
 * In Node/jsdom without IndexedDB we fall back to a process-local Map. Tests
 * using `fake-indexeddb` get the real IDB code path automatically.
 */

import { postSync } from './api';

const DB_NAME = 'wiazary-sync-outbox';
const DB_VERSION = 1;
const STORE_NAME = 'outbox';
const LOCK_NAME = 'outbox-drainer';
const CHANNEL_NAME = 'outbox';
const DRAIN_INTERVAL_MS = 30_000;
const MAX_ATTEMPTS = 10;
// 1s, 4s, 16s, 64s, 256s capped at 300s (5 min).
const BACKOFF_BASE_MS = 1_000;
const BACKOFF_FACTOR = 4;
const BACKOFF_CAP_MS = 5 * 60_000;

// ─── IndexedDB plumbing ─────────────────────────────────────────────────────

function getIDB() {
  if (typeof indexedDB !== 'undefined') return indexedDB;
  if (typeof globalThis !== 'undefined' && globalThis.indexedDB) return globalThis.indexedDB;
  return null;
}

let _dbPromise = null;

function openDb() {
  if (_dbPromise) return _dbPromise;
  const idb = getIDB();
  if (!idb) {
    _dbPromise = Promise.reject(new Error('IndexedDB unavailable'));
    return _dbPromise;
  }
  _dbPromise = new Promise((resolve, reject) => {
    const req = idb.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: 'key' });
        store.createIndex('byNextAttemptAt', 'nextAttemptAt');
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
    req.onblocked = () => reject(new Error('IndexedDB upgrade blocked'));
  });
  return _dbPromise;
}

function tx(mode, fn) {
  return openDb().then((db) => new Promise((resolve, reject) => {
    const t = db.transaction(STORE_NAME, mode);
    const store = t.objectStore(STORE_NAME);
    let result;
    Promise.resolve(fn(store)).then((r) => { result = r; }).catch(reject);
    t.oncomplete = () => resolve(result);
    t.onerror = () => reject(t.error);
    t.onabort = () => reject(t.error || new Error('IDB tx aborted'));
  }));
}

function reqToPromise(req) {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
}

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Enqueue an envelope for later replay. Resolves immediately after the write
 * lands in IndexedDB; never throws to the caller (a failure to enqueue is
 * logged but does not interrupt the Firebase write that produced it).
 */
export async function enqueue(collectionName, envelope) {
  const key = `${collectionName}:${envelope.id}:${envelope.serverWriteTs || Date.now()}:${Math.random().toString(36).slice(2, 8)}`;
  const now = Date.now();
  const item = {
    key,
    collection: collectionName,
    envelope,
    attempt: 0,
    nextAttemptAt: now,        // eligible immediately
    lastAttemptAt: 0,
    lastError: null,
    enqueuedAt: now,
    requires_manual_review: false,
  };
  try {
    await tx('readwrite', (store) => reqToPromise(store.put(item)));
    notifyDrainer('enqueued');
  } catch (err) {
    // IDB unavailable / quota exceeded / etc. Log only — never propagate.
    // eslint-disable-next-line no-console
    console.warn('[outbox] enqueue failed', collectionName, envelope.id, err);
  }
  return item.key;
}

/**
 * Snapshot of all queue items (sorted by enqueuedAt asc). For diagnostics
 * and tests.
 */
export async function peekAll() {
  try {
    return await tx('readonly', (store) => reqToPromise(store.getAll()));
  } catch (_) {
    return [];
  }
}

/**
 * Number of items currently pending. Convenience for tests / diag panels.
 */
export async function size() {
  try {
    return await tx('readonly', (store) => reqToPromise(store.count()));
  } catch (_) {
    return 0;
  }
}

/**
 * One drain pass — attempts every eligible item exactly once. Returns
 * `{ attempted, succeeded, deferred, failed, manualReview }`.
 *
 * Exposed so leader-less environments (tests, manual triage UIs) can pull
 * the queue without spinning the long-lived loop.
 */
export async function drainOnce(now = Date.now()) {
  const items = await peekAll();
  let attempted = 0, succeeded = 0, deferred = 0, failed = 0, manualReview = 0;

  for (const item of items) {
    if (item.requires_manual_review) { manualReview++; continue; }
    if (item.attempt >= MAX_ATTEMPTS) {
      // Belt-and-suspenders: prior pass should have set the flag, but if not, set it now.
      await markManualReview(item.key, item.lastError);
      manualReview++;
      continue;
    }
    if (item.nextAttemptAt > now) { deferred++; continue; }

    attempted++;
    try {
      await postSync(item.collection, item.envelope);
      await remove(item.key);
      succeeded++;
    } catch (err) {
      // Signed-out / 401 / 403 is transient (admin closed the tab overnight,
      // token rotation race, etc.). Burning attempts here would push the item
      // to manual_review after ~10 logout cycles, which is hostile — defer
      // with normal backoff but DO NOT increment `attempt`, so once auth is
      // back the item drains immediately on the next eligible tick.
      if (err && err.isAuth) {
        const delay = Math.min(BACKOFF_BASE_MS * Math.pow(BACKOFF_FACTOR, item.attempt), BACKOFF_CAP_MS);
        await tx('readwrite', (store) => reqToPromise(store.put({
          ...item,
          lastAttemptAt: now,
          nextAttemptAt: now + delay,
          lastError: errToString(err),
        })));
        deferred++;
        continue;
      }
      failed++;
      const nextAttempt = item.attempt + 1;
      if (nextAttempt >= MAX_ATTEMPTS) {
        await markManualReview(item.key, errToString(err));
        manualReview++;
      } else {
        const delay = Math.min(BACKOFF_BASE_MS * Math.pow(BACKOFF_FACTOR, item.attempt), BACKOFF_CAP_MS);
        await tx('readwrite', (store) => reqToPromise(store.put({
          ...item,
          attempt: nextAttempt,
          lastAttemptAt: now,
          nextAttemptAt: now + delay,
          lastError: errToString(err),
        })));
      }
    }
  }
  return { attempted, succeeded, deferred, failed, manualReview };
}

async function remove(key) {
  try {
    await tx('readwrite', (store) => reqToPromise(store.delete(key)));
  } catch (_) { /* ignore */ }
}

/**
 * Admin triage: remove a single item by key. Returns true if it existed.
 * Used by OutboxMonitor's per-row "Discard" action.
 */
export async function removeItem(key) {
  if (!key) return false;
  try {
    const exists = await tx('readonly', (store) => reqToPromise(store.get(key)));
    if (!exists) return false;
    await tx('readwrite', (store) => reqToPromise(store.delete(key)));
    return true;
  } catch (_) {
    return false;
  }
}

/**
 * Admin triage: remove ONLY items flagged requires_manual_review === true.
 * Returns the count removed. Safe to call repeatedly.
 *
 * Avoids `Promise.all` inside the IDB txn — awaiting a Promise inside an IDB
 * transaction is a documented foot-gun (txn auto-commits on the next microtask
 * with no pending requests, causing later deletes to throw TransactionInactiveError).
 * Instead, fire all delete requests synchronously and let `tx()` resolve on `oncomplete`.
 */
export async function clearFailed() {
  try {
    const items = await peekAll();
    const failed = items.filter((i) => i && i.requires_manual_review === true);
    if (failed.length === 0) return 0;
    await tx('readwrite', (store) => {
      for (const i of failed) store.delete(i.key);
    });
    return failed.length;
  } catch (_) {
    return 0;
  }
}

/**
 * Admin nuclear option: wipe the entire queue. Returns the count removed.
 * UI MUST gate this behind double confirmation — pending writes will be
 * lost (Firebase is canonical, so the data is safe but PostgreSQL drifts
 * until the next reconciliation pass).
 */
export async function clearAll() {
  try {
    const items = await peekAll();
    if (items.length === 0) return 0;
    await tx('readwrite', (store) => reqToPromise(store.clear()));
    return items.length;
  } catch (_) {
    return 0;
  }
}

/**
 * Admin triage: reset an item's backoff so the next drain pass tries it again.
 * Clears the manual-review flag too — useful when the failure was transient
 * (e.g. API was down, now it's back). Returns true if the item was found.
 *
 * `lastError` is preserved as `previousLastError` so the admin still has a
 * diagnostic if the retry fails again with the same error. A successful retry
 * (drained ok) removes the item entirely, taking both fields with it.
 */
export async function retryItem(key, now = Date.now()) {
  if (!key) return false;
  try {
    return await tx('readwrite', (store) => new Promise((resolve, reject) => {
      const getReq = store.get(key);
      getReq.onsuccess = () => {
        const cur = getReq.result;
        if (!cur) { resolve(false); return; }
        const putReq = store.put({
          ...cur,
          attempt: 0,
          nextAttemptAt: now,
          lastAttemptAt: 0,
          previousLastError: cur.lastError || cur.previousLastError || null,
          lastError: null,
          requires_manual_review: false,
        });
        putReq.onsuccess = () => { notifyDrainer('retry'); resolve(true); };
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    }));
  } catch (_) {
    return false;
  }
}

/**
 * Admin manual trigger: wake any drainer (local or cross-tab leader) right now,
 * instead of waiting for the 30s tick. Useful after a Retry click.
 */
export function forceDrainNow() {
  notifyDrainer('manual');
}

async function markManualReview(key, errStr) {
  try {
    await tx('readwrite', (store) => new Promise((resolve, reject) => {
      const getReq = store.get(key);
      getReq.onsuccess = () => {
        const cur = getReq.result;
        if (!cur) { resolve(); return; }
        const putReq = store.put({
          ...cur,
          attempt: MAX_ATTEMPTS,
          requires_manual_review: true,
          lastError: errStr || cur.lastError,
        });
        putReq.onsuccess = () => resolve();
        putReq.onerror = () => reject(putReq.error);
      };
      getReq.onerror = () => reject(getReq.error);
    }));
  } catch (_) { /* ignore */ }
}

function errToString(err) {
  if (!err) return null;
  return (err.message || String(err)).slice(0, 500);
}

// ─── Leader election + drainer loop ─────────────────────────────────────────

let _channel = null;
let _loopActive = false;
let _stopSignal = false;
let _wakeupResolver = null;

function getChannel() {
  if (_channel) return _channel;
  if (typeof BroadcastChannel === 'undefined') return null;
  try {
    _channel = new BroadcastChannel(CHANNEL_NAME);
    return _channel;
  } catch (_) {
    return null;
  }
}

function notifyDrainer(reason) {
  const ch = getChannel();
  if (ch) {
    try { ch.postMessage({ type: 'wakeup', reason, at: Date.now() }); } catch (_) { /* ignore */ }
  }
  // Local wakeup for leader running in THIS tab.
  if (_wakeupResolver) {
    const r = _wakeupResolver;
    _wakeupResolver = null;
    r();
  }
}

function sleepWithWakeup(ms) {
  return new Promise((resolve) => {
    const timer = setTimeout(() => {
      _wakeupResolver = null;
      resolve();
    }, ms);
    _wakeupResolver = () => { clearTimeout(timer); resolve(); };
  });
}

/**
 * Run the drainer loop until stop() is called. Tries to take the Web Lock;
 * if `navigator.locks` is unavailable, runs unconditionally in this tab.
 *
 * Should be called once at app boot. Idempotent — repeated calls are no-ops
 * while a loop is already active in this tab.
 */
export async function startDrainer() {
  if (_loopActive) return;
  _loopActive = true;
  _stopSignal = false;

  // Wake up on browser regaining connectivity and on cross-tab enqueues.
  if (typeof window !== 'undefined') {
    window.addEventListener('online', () => notifyDrainer('online'));
  }
  const ch = getChannel();
  if (ch) ch.onmessage = (ev) => {
    if (ev.data && ev.data.type === 'wakeup') notifyDrainer('broadcast');
  };

  const runLoop = async () => {
    while (!_stopSignal) {
      try {
        await drainOnce();
      } catch (err) {
        // eslint-disable-next-line no-console
        console.warn('[outbox] drainOnce errored', err);
      }
      if (_stopSignal) break;
      await sleepWithWakeup(DRAIN_INTERVAL_MS);
    }
  };

  if (typeof navigator !== 'undefined' && navigator.locks && typeof navigator.locks.request === 'function') {
    // Web Locks path — only one tab wins; others wait inside the request.
    // We do NOT await this — caller doesn't need to block.
    navigator.locks.request(LOCK_NAME, { mode: 'exclusive' }, async () => {
      await runLoop();
    }).catch((err) => {
      // eslint-disable-next-line no-console
      console.warn('[outbox] Web Locks request rejected, draining without lock', err);
      return runLoop();
    });
  } else {
    // No Web Locks — fall through to single-tab mode.
    // eslint-disable-next-line no-console
    console.info('[outbox] navigator.locks unavailable, every tab drains (idempotent on backend)');
    runLoop();
  }
}

/**
 * Stop the drainer loop. Used by tests and (in theory) app teardown.
 * The Web Lock is released when the runLoop callback returns.
 */
export function stopDrainer() {
  _stopSignal = true;
  _loopActive = false;
  if (_wakeupResolver) {
    const r = _wakeupResolver;
    _wakeupResolver = null;
    r();
  }
}

// ─── Test helpers ───────────────────────────────────────────────────────────

export async function _clearForTest() {
  _stopSignal = true;
  _loopActive = false;
  _wakeupResolver = null;
  if (_channel) {
    try { _channel.close(); } catch (_) { /* ignore */ }
    _channel = null;
  }
  // Close any cached DB handle so deleteDatabase isn't blocked. We MUST resolve
  // the prior promise before nulling it, because vitest test isolation reruns
  // the module and the cached promise may already be resolved.
  if (_dbPromise) {
    try {
      const db = await _dbPromise.catch(() => null);
      if (db && typeof db.close === 'function') db.close();
    } catch (_) { /* ignore */ }
  }
  _dbPromise = null;

  // Best-effort drop the DB so each test starts clean. Wrap in a 1s timeout so
  // a misbehaving fake-indexeddb / browser cannot stall vitest's beforeEach.
  const idb = getIDB();
  if (!idb) return;
  await new Promise((resolve) => {
    let settled = false;
    const finish = () => { if (!settled) { settled = true; resolve(); } };
    const safetyTimer = setTimeout(finish, 1000);
    try {
      const req = idb.deleteDatabase(DB_NAME);
      req.onsuccess = () => { clearTimeout(safetyTimer); finish(); };
      req.onerror = () => { clearTimeout(safetyTimer); finish(); };
      req.onblocked = () => { clearTimeout(safetyTimer); finish(); };
    } catch (_) {
      clearTimeout(safetyTimer);
      finish();
    }
  });
}

export const _internals = {
  MAX_ATTEMPTS,
  BACKOFF_BASE_MS,
  BACKOFF_FACTOR,
  BACKOFF_CAP_MS,
  DRAIN_INTERVAL_MS,
};
